import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  ArrowLeft,
  Loader2,
  BookOpen,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Minus,
  Sparkles,
  GraduationCap,
  MessageCircle,
  MoreVertical,
  LifeBuoy,
  SkipForward,
  Flag,
} from "lucide-react";

import { GET_SESSION } from "@/graphql/queries/sessions";
import {
  SUBMIT_STUDENT_ANSWERS,
  CHECK_STUDENT_ANSWER,
  COMPLETE_SESSION,
  REQUEST_ANSWER_RECHECK,
  SKIP_EXERCISE,
} from "@/graphql/mutations/sessions";
import { GET_STUDENT_AVATAR_CREDITS } from "@/graphql/queries/students";
import { useAvatarStudio } from "@/components/avatar-studio";
import { SessionComplete } from "@/components/session-complete";
import { SessionFinishButton, SessionReview } from "@/components/session-review";
import { QuestionClockBar } from "@/components/question-clock";
import { useAuth } from "@/lib/auth/AuthContext";
import { useCelebration } from "@/components/celebrations";
import { toCelebrationEvents, type GamificationResult } from "@/lib/gamification";
import { useQuestionClock } from "@/hooks/use-question-clock";
import { useSessionTimer } from "@/hooks/use-session-timer";
import { useDiscussions } from "@/hooks/use-discussions";
import { useSlideParam } from "@/hooks/use-slide-param";
import { useTheoryView } from "@/hooks/use-theory-view";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MathContent } from "@/components/math/math-content";
import {
  TheoryView,
  hasTheoryExample,
  hasTheoryKeyPoints,
  hasTheoryBeyondExample,
} from "@/components/theory-view";
import { ChoiceProblem, isChoiceProblem } from "@/components/math/choice-problem";
import { SkillCategoryBadge } from "@/components/skill-category-badge";
import { DiscussionPanel } from "@/components/discussion/discussion-panel";
import { NewSkillModal } from "@/components/new-skill-modal";
import { SupportDialog } from "@/components/support/support-dialog";
import { ReportProblem } from "@/components/report-problem";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  checkAnswerAdvanced,
  checkChoiceAnswer,
  detectMultiAnswers,
  checkMultiAnswerAdvanced,
  splitFillBlanks,
  parseFillBlanks,
  checkFillBlanksAdvanced,
  type CheckResult,
  type MultiCheckResult,
} from "@/lib/answer-checking";
import {
  buildAnswerFormatsFor,
  composeAnswer,
  toInputValue,
  type AnswerFormat,
} from "@/lib/answer-format";
import { AnswerAffix, AnswerInput } from "@/components/math/answer-input";
import { AiBadge } from "@/components/discussion/ai-disclosure";
import { SLIDE_ADVANCE_MS } from "@/lib/advance";
import type { Exercise } from "@/graphql/types";

type Verdict = "CORRECT" | "INCORRECT" | "PARTIAL";

/**
 * What the „nauja tema" nudge is tracked by: the skill, since one theory file
 * serves every question of it. Falls back to the question's own id for the
 * exercise whose skill could not be resolved — one nudge that cannot be shared
 * is better than one shared by everything with no skill key.
 *
 * Module scope because it closes over nothing; inside the component it would be
 * a new function every render and a missing dependency of the memo below.
 */
const nudgeKeyOf = (exercise: Exercise) => exercise.skillKey || exercise.id;

export default function SolveSessionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role } = useAuth();
  const { data, loading } = useQuery(GET_SESSION, {
    variables: { id },
    skip: !id,
  });

  const [submitAnswers, { loading: isSubmitting }] = useMutation(SUBMIT_STUDENT_ANSWERS);
  const [checkStudentAnswer] = useMutation(CHECK_STUDENT_ANSWER);
  const [completeSession] = useMutation(COMPLETE_SESSION);
  const [requestRecheck] = useMutation(REQUEST_ANSWER_RECHECK);
  const [skipExercise] = useMutation(SKIP_EXERCISE);
  const { celebrateMultiple, fireConfetti } = useCelebration();

  const [gamificationResult, setGamificationResult] = useState<GamificationResult | null>(null);
  // Problems the learner has disputed, so the link does not offer itself twice.
  const [recheckRequested, setRecheckRequested] = useState<Set<string>>(new Set());
  const [reported, setReported] = useState<Set<string>>(new Set());
  // Which question's report form is open. One at a time, and opened only from
  // that question's „…" menu.
  const [reportOpen, setReportOpen] = useState<string | null>(null);
  // „Reikia pagalbos su programa" — the app itself, not this question. In the
  // „…" menu beside skip and report, deliberately NOT in the help-ladder row
  // below the question: that row is one door on purpose, and a fourth link
  // there rebuilds the choice the ladder was built to remove.
  const [supportOpen, setSupportOpen] = useState(false);
  // Questions the learner left behind. Deliberately not a value in
  // `checkedProblems`: a skip is not a verdict, and everything reading that map
  // — the score, the card colour, the progress rail — would have to learn to
  // ignore one of its own values.
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  // Of those, the ones the server has put to rest — the question will not be
  // back next session. Answered by `skipExercise`, so it is only ever known for
  // a skip made here; a reload loses it, which is right, because the sentence it
  // carries belongs to the moment the child pressed the button.
  const [parked, setParked] = useState<Set<string>>(new Set());
  const [newAvatarUrl, setNewAvatarUrl] = useState<string | null>(null);
  // Whether completeSession has answered. The finish screen paints before it
  // does — deliberately, so the score is instant — but "is a letter waiting?" is
  // counted from completed sessions, so asking any earlier answers about the
  // world one session ago.
  const [completionSettled, setCompletionSettled] = useState(false);

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  // Which exercise is on screen lives in the URL (?task=N) so it survives a refresh.
  //
  // One slide past the last question is the review screen — where the session is
  // handed in. Counted here so it is a real, linkable slide rather than a mode
  // the last question can be in: „Pateikti" used to live in the last question's
  // own navigation bar, where the tutor could disable it (see `SessionReview`).
  const questionCount: number = data?.session?.exercises?.length ?? 0;
  const [currentSlide, setCurrentSlide, slidePinned] = useSlideParam(
    questionCount > 0 ? questionCount + 1 : 0
  );
  // The theory panel above the question starts closed everywhere; this holds only
  // the keys something opened — the student tapping the header, or the new-topic
  // modal. Deliberately not a wrong answer: that opens the concept card inside
  // the question instead (see `revealTheoryFor`), which is where an explanation
  // can be read without scrolling the answer it explains off the screen.
  const [theoryToggles, setTheoryToggles] = useState<Record<string, boolean>>({});
  // Hints revealed *after* an answer, never as a door before one: a wrong or
  // partial answer, a conversation that has ended, or a tutor that failed to
  // open. The hint is the wrong scaffold to offer beforehand — measured over
  // 3,000 level-1 hints, 27% open by naming the operation to perform, which on a
  // word problem is the whole skill being assessed — but afterwards that step is
  // already graded and naming it is exactly the explanation owed. Set by the
  // handlers below and rebuilt from server state on restore.
  const [revealedHints, setRevealedHints] = useState<Set<string>>(new Set());
  // How far up the help ladder each problem has been taken: absent = not asked,
  // 1 = the concept card is open, 2 = the tutor has been called in.
  //
  // One door, two steps, replacing three similar-sounding links („Rodyti
  // užuominą" / „Parodyk pavyzdį" / „Noriu suprasti"). A child could not tell
  // them apart, and the only one that costs marks looked exactly like the two
  // that are free — so the expensive door competed as an equal first choice.
  const [helpSteps, setHelpSteps] = useState<Record<string, number>>({});
  // Which concept cards have been opened out into the skill's whole theory. The
  // card is narrowed to this question's concept on purpose, but a child who reads
  // it and still does not follow has one more thing to try before the tutor — and
  // it costs nothing, so it must not be the door that costs marks.
  const [conceptExpanded, setConceptExpanded] = useState<Record<string, boolean>>({});
  const [checkedProblems, setCheckedProblems] = useState<Record<string, Verdict>>({});
  // Answered right, but after the clock. PARTIAL already means „partly right" on
  // a multi-blank question, so without this the note under the answer could not
  // tell a child which of the two happened to them.
  const [lateProblems, setLateProblems] = useState<Set<string>>(new Set());
  const [subAnswerResults, setSubAnswerResults] = useState<Record<string, CheckResult[]>>({});
  const [restoredFromServer, setRestoredFromServer] = useState(false);
  const [showNewSkillModal, setShowNewSkillModal] = useState(false);
  const inputRefs = useRef<Map<string, HTMLElement>>(new Map());

  /**
   * Put the answer field back in front of the child, and the keyboard in it.
   *
   * `inputRefs` already holds the one element that stands for „where the answer
   * goes" whichever shape the question is — a single input, the first of a
   * multi, the first blank inside the sentence, or the first choice button — so
   * one lookup covers all four rather than four branches that can disagree.
   *
   * A frame first: this is called from a handler that resolves a conversation in
   * the same commit, and measuring before React has laid that out scrolls to
   * where the field used to be. `preventScroll` on the focus so it does not
   * fight the smooth scroll above it — the same pairing the chat panel uses.
   */
  const returnToAnswer = useCallback((exerciseId: string) => {
    requestAnimationFrame(() => {
      const el = inputRefs.current.get(exerciseId);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus({ preventScroll: true });
    });
  }, []);

  const session = data?.session;
  const studentId: string | undefined = session?.student?.id;

  // Whether finishing has earned them an avatar. Read here so the completion
  // screen can offer it the moment the points land. Offer, not spend: the style
  // is always the learner's pick, so nothing is generated behind their back.
  const avatarStudio = useAvatarStudio(studentId, {
    withGallery: false,
    successToast: false,
    onGenerated: (avatar) => {
      setNewAvatarUrl(avatar.url);
      fireConfetti("default");
    },
  });

  // Track active working time while the student is solving.
  useSessionTimer(id, session?.status === "GENERATED" && !submitted);

  const theoryView = useTheoryView(id);

  // AI adjudication + tutoring chat (opens instantly, adjudicates in background).
  const revealTheoryRef = useRef<(exerciseId: string) => void>(() => {});
  const advanceRef = useRef<(exerciseId: string) => void>(() => {});
  const tutor = useDiscussions({
    sessionId: id,
    onResultChange: (exerciseId, result, viaChat) => {
      setCheckedProblems((prev) => ({ ...prev, [exerciseId]: result }));
      // The AI accepted the answer outright — there is no conversation to read,
      // so this is simply a correct answer and carries the student on.
      if (result === "CORRECT" && !viaChat) advanceRef.current(exerciseId);
    },
    onDegraded: (exerciseId) => {
      setRevealedHints((prev) => new Set(prev).add(exerciseId));
      revealTheoryRef.current(exerciseId);
    },
    onClosed: (exerciseId, branch) => {
      setRevealedHints((prev) => new Set(prev).add(exerciseId));
      revealTheoryRef.current(exerciseId);
      // „Supratau — bandau atsakyti →" moved nothing, and the resolved panel then
      // said „Dabar įrašyk atsakymą pats." while pointing at a field several
      // turns' worth of conversation away. Only for HELP: a DISCUSSION ends after
      // the answer, where the field is disabled and the next move is „Toliau".
      //
      // Not left to the focus effect below — it is keyed on the current slide and
      // deliberately stands down while a conversation is OPEN, so nothing re-runs
      // it when one closes.
      if (branch === "HELP") returnToAnswer(exerciseId);
    },
  });

  // How each expected answer is entered (unit label, compound fields, clock),
  // keyed like `answers` entries. Units are appended when the answer is
  // checked or submitted, so the student only types numbers.
  const answerFormats = useMemo<Record<string, AnswerFormat>>(() => {
    if (!session) return {};
    return buildAnswerFormatsFor(session.exercises);
  }, [session]);

  // A wrong answer opens the concept card, not the whole theory file.
  //
  // It used to expand the accordion above the question, and that is the one place
  // theory cannot go at this moment: a `.concept` block runs to a median ~1000
  // characters, so opening it pushes the question, the answer the child just gave
  // and the correction that explains it off the screen — leaving them reading a
  // wall of explanation with nothing on screen to attach it to. The card is the
  // narrowed version (this question's concept: one example, one explanation, one
  // rule) and it renders *inside* the question, above the answer, so the thing
  // being explained stays in view. „Rodyti visą teoriją" is there for the child
  // who wants the rest.
  //
  // Deliberately the same slot „Noriu suprasti" opens (`helpSteps`) rather than a
  // second box: it is the same card, and two sources of truth for one element
  // would let a wrong answer and a help request stack it twice.
  const revealTheoryFor = useCallback(
    (exerciseId: string) => {
      const exercise = session?.exercises.find((ex: Exercise) => ex.id === exerciseId);
      if (!exercise || !hasTheoryExample(exercise.theory)) return;
      setHelpSteps((prev) =>
        (prev[exerciseId] ?? 0) >= 1 ? prev : { ...prev, [exerciseId]: 1 }
      );
    },
    [session]
  );

  // Restore previously checked answers + discussions from server data
  useEffect(() => {
    if (!session || restoredFromServer) return;
    const restoredAnswers: Record<string, string> = {};
    const restoredChecked: Record<string, Verdict> = {};
    const restoredHints = new Set<string>();
    const restoredRechecks = new Set<string>();
    // The concept card a wrong answer opened. Rebuilt here for the same reason
    // the hints are: it is not a thing the child chose to open, so a reload
    // dropping it takes away the explanation of an answer that is still on screen
    // being marked wrong.
    const restoredHelp: Record<string, number> = {};

    // Separate from the loop below, which skips unanswered questions: a broken
    // figure is reported *before* answering, and that report has to survive a
    // reload as much as any other.
    const restoredReports = new Set<string>(
      session.exercises.filter((e: Exercise) => e.reportedAt).map((e: Exercise) => e.id)
    );
    if (restoredReports.size > 0) setReported(restoredReports);

    // Same reason, and the loop below would miss these twice over: a skip is
    // neither a verdict nor an answer, so it has no `userAnswer` to restore.
    // Without this a reload puts the skipped questions back in front of the
    // child as unanswered and the session cannot be finished again.
    const restoredSkips = new Set<string>(
      session.exercises.filter((e: Exercise) => e.result === "SKIPPED").map((e: Exercise) => e.id)
    );
    if (restoredSkips.size > 0) setSkipped(restoredSkips);

    // Why a right answer is sitting there marked PARTIAL. The clock is long gone
    // by the time a reload happens, so without this the note vanishes and the
    // grade becomes the mystery the visible countdown exists to prevent.
    const restoredLate = new Set<string>(
      session.exercises.filter((e: Exercise) => e.answeredLate).map((e: Exercise) => e.id)
    );
    if (restoredLate.size > 0) setLateProblems(restoredLate);

    for (const exercise of session.exercises) {
      if (!exercise.result || exercise.result === "PENDING" || exercise.userAnswer == null) continue;

      const result: Verdict =
        exercise.result === "CORRECT" ? "CORRECT" : exercise.result === "PARTIAL" ? "PARTIAL" : "INCORRECT";
      restoredChecked[exercise.id] = result;

      // Stored answers carry their unit; the input keeps it beside the field.
      const restore = (key: string, value: string) => {
        restoredAnswers[key] = toInputValue(value, answerFormats[key]);
      };

      if (exercise.answerType === "fill_blanks") {
        const userParts = exercise.userAnswer.split("|");
        for (let i = 0; i < userParts.length; i++) {
          restore(`${exercise.id}__${i}`, userParts[i] || "");
        }
      } else {
        const multiAnswers = exercise.expectedAnswer
          ? detectMultiAnswers(exercise.expectedAnswer, exercise.problemText, exercise.choices)
          : null;
        if (multiAnswers) {
          const userParts = exercise.userAnswer.split(", ");
          for (let i = 0; i < multiAnswers.length; i++) {
            restore(`${exercise.id}__${i}`, userParts[i] || "");
          }
        } else {
          restore(exercise.id, exercise.userAnswer);
        }
      }

      if (exercise.recheckRequestedAt) restoredRechecks.add(exercise.id);

      // Only auto-reveal hint once any discussion is concluded.
      const disc = exercise.discussion;
      if (result === "INCORRECT" && exercise.hint && (!disc || disc.status !== "OPEN")) {
        restoredHints.add(exercise.id);
      }

      if (result !== "CORRECT" && hasTheoryExample(exercise.theory)) {
        restoredHelp[exercise.id] = 1;
      }
    }

    if (Object.keys(restoredChecked).length > 0) {
      setAnswers(restoredAnswers);
      setCheckedProblems(restoredChecked);
      setRevealedHints(restoredHints);
      setRecheckRequested(restoredRechecks);
      setHelpSteps(restoredHelp);
    }
    tutor.restore(session.exercises);
    setRestoredFromServer(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, restoredFromServer, answerFormats]);

  // Coming back to an unfinished session lands on the first question that is
  // still open, not on question 1. Otherwise a child who stopped at question
  // eight has to press „Toliau" past seven questions they have already answered
  // to reach the one they left — and the first thing the session shows them is
  // work they have finished, which reads as having lost their place.
  //
  // Skipped counts as settled: they went past it on purpose, and the inputs stay
  // live, so it is still reachable by walking back. Everything settled means the
  // review screen, which is the only one carrying the finish button — a child
  // coming back to a session they answered to the end lands on the button they
  // came back to press.
  //
  // Only when the URL named no slide — `?task=N` is somebody's explicit choice
  // (a refresh, a shared link, the browser's own restore) and must win.
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current || slidePinned || !session) return;
    const exercises = session.exercises as Exercise[];
    if (exercises.length === 0) return;
    landed.current = true;

    const isSettled = (exercise: Exercise) =>
      exercise.result === "SKIPPED" ||
      (!!exercise.result && exercise.result !== "PENDING" && exercise.userAnswer != null);

    const firstOpen = exercises.findIndex((exercise) => !isSettled(exercise));
    const target = firstOpen === -1 ? exercises.length : firstOpen;
    if (target > 0) setCurrentSlide(target);
  }, [session, slidePinned, setCurrentSlide]);

  // Announce a new topic once per session. The dialog no longer opens the theory
  // for them — see `NewSkillModal` and the nudge below.
  useEffect(() => {
    if (!session?.newSkillKey || session.status !== "GENERATED") return;
    if (session.exercises.length === 0 || submitted) return;

    const seenKey = `new-skill-seen-${session.id}`;
    try {
      if (localStorage.getItem(seenKey)) return;
      localStorage.setItem(seenKey, "1");
    } catch {
      /* private mode — show it, just don't remember */
    }
    setShowNewSkillModal(true);
  }, [session?.id, session?.newSkillKey, session?.status, session?.exercises.length, submitted]);

  // ── The „nauja tema" nudge ──
  //
  // A new topic used to arrive with an interstitial offering to open its theory,
  // and theory opened *for* a child is theory they scroll past — 24% engagement
  // when it auto-opened, and it puts a wall of prose between them and a question
  // they have not read yet. So the panel asks for itself instead: it pulses
  // (`.theory-nudge`) until the child has either opened it or got a question of
  // the topic right.
  //
  // Both exits matter and they say different things. Opening it is „I have taken
  // the hint"; a correct answer is „I did not need it" — and going on pulsing at a
  // child who is already getting the topic right is how you teach them to stop
  // seeing it. Per SKILL, not per question, because one theory serves them all
  // (`nudgeKeyOf`, at the top of the file).

  // Opened this session. Latches: closing it again is not un-reading it.
  const [theoryOpened, setTheoryOpened] = useState<Set<string>>(new Set());

  // Earned out of the way. Derived rather than stored, so it needs no restore
  // pass of its own — `checkedProblems` is already rebuilt from the server, and
  // `theoryOpenedAt` is the server's own latch (`RecordTheoryView`). Without that
  // second half a reload would put the nudge back on a child who had read the
  // theory, which is the one place it must not reappear: they did not raise it,
  // so nothing on screen would explain why it is back.
  const nudgeSettled = useMemo(() => {
    const settled = new Set<string>();
    if (!session) return settled;
    for (const exercise of session.exercises as Exercise[]) {
      if (checkedProblems[exercise.id] === "CORRECT" || exercise.theoryOpenedAt) {
        settled.add(nudgeKeyOf(exercise));
      }
    }
    return settled;
  }, [session, checkedProblems]);

  // Auto-focus the first unanswered input of the current slide. Keyed on the
  // exercise id, not the session object: a chat reply updates the cached
  // session, and that must not pull focus out of the chat.
  const focusedExerciseId = session?.exercises?.[currentSlide]?.id;
  useEffect(() => {
    if (!session) return;
    const exercise = session.exercises[currentSlide];
    if (!exercise) return;
    // The tutor owns the keyboard while a conversation is open.
    if (tutor.discussions[exercise.id]?.status === "OPEN") return;
    if (!exercise.expectedAnswer || checkedProblems[exercise.id]) return;
    const t = setTimeout(() => {
      inputRefs.current.get(exercise.id)?.focus({ preventScroll: true });
    }, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedExerciseId, restoredFromServer]);

  // The countdown on a fluency question. One clock, not one per question: only
  // the current slide is rendered, and it must be declared up here with the
  // other hooks rather than beside `currentExercise` — everything below the
  // early returns runs a different number of hooks on a finished session, which
  // is the crash documented at those returns.
  //
  // `timeLimit` is null on all but the drill questions, and a null limit makes
  // every branch of this inert.
  const clockExercise = session?.exercises?.[currentSlide];
  const questionClock = useQuestionClock(
    clockExercise?.timeLimit,
    !!clockExercise,
    // Already answered, already skipped, or the tutor is mid-conversation about
    // it — none of those are a child racing a clock.
    !!(clockExercise && (checkedProblems[clockExercise.id] || skipped.has(clockExercise.id)))
  );

  // `checkProblem` reads the clock through a ref, the same way it reaches the
  // advance and the activity timer: the reading changes every second, and a
  // callback rebuilt on every tick would re-register every input handler under
  // it once a second for the whole session.
  //
  // The exercise id travels with it so a reading can never be credited to the
  // wrong question — the clock belongs to whichever slide is on screen, and
  // `checkProblem` is called with an id.
  const clockRef = useRef({
    exerciseId: clockExercise?.id,
    elapsed: questionClock.elapsed,
    voided: questionClock.voided,
    expired: questionClock.expired,
    stop: questionClock.stop,
  });
  clockRef.current = {
    exerciseId: clockExercise?.id,
    elapsed: questionClock.elapsed,
    voided: questionClock.voided,
    expired: questionClock.expired,
    stop: questionClock.stop,
  };

  // Asking for help ends the timing rather than pausing it. Opening the concept
  // card or the tutor is the child saying they do not know this one — and the
  // question of a fluency clock is how fast they recall it, which they have just
  // answered. Pausing would leave the clock running afterwards and dock them for
  // the one thing this app most wants them to do.
  const clockVoid = questionClock.voidClock;
  const helpOpenFor = clockExercise ? (helpSteps[clockExercise.id] ?? 0) > 0 : false;
  useEffect(() => {
    if (helpOpenFor) clockVoid();
  }, [helpOpenFor, clockVoid]);

  // The discussion hook needs this before it is declared below.
  useEffect(() => {
    revealTheoryRef.current = revealTheoryFor;
  }, [revealTheoryFor]);

  // Where a finished question leads: the next question, or — from the last one —
  // the review screen. There is always somewhere to go while a question is on
  // screen, which is the point: „move on" is what the tutor's panel offers when
  // a conversation is over, and on the last question it used to offer nothing.
  const hasNextSlide = useCallback(
    (): boolean => currentSlide < (session?.exercises.length ?? 0),
    [currentSlide, session]
  );

  const goToNextSlide = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentSlide((s) => s + 1);
  }, [setCurrentSlide]);

  // A first-attempt correct answer carries the student onward by itself.
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    []
  );

  const advanceAfterCorrect = useCallback(
    (_solvedExerciseId: string) => {
      if (!hasNextSlide()) return;
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(goToNextSlide, SLIDE_ADVANCE_MS);
    },
    [hasNextSlide, goToNextSlide]
  );

  // The tutor is declared above this, so it reaches the advance through a ref.
  useEffect(() => {
    advanceRef.current = advanceAfterCorrect;
  }, [advanceAfterCorrect]);

  const checkProblem = useCallback(
    (
      exerciseId: string,
      expectedAnswer: string,
      problemText: string,
      directAnswer?: string,
      answerType?: string | null,
      // Passed so the grading path reads the question the same way the
      // rendering above it does — a choice question is one answer, never a
      // list of sub-answers split on its commas.
      choices?: string[] | null
    ) => {
      // Reading time only counts before the first attempt. Nothing is shut
      // afterwards: the theory is only ever open because somebody opened it,
      // and closing that would be undoing their choice.
      theoryView.markAttempted(exerciseId);

      // What the student typed, with the displayed unit appended.
      const answerAt = (key: string) => composeAnswer(answers[key] || "", answerFormats[key]);
      // The question's own checking rules (a capital-letter question counts case).
      const rules = (session?.exercises as Exercise[] | undefined)?.find((ex) => ex.id === exerciseId)?.answerRules;

      // Only a clean first hit moves on by itself — anything the tutor touched
      // leaves the student in place to read what they were told.
      const conversation = tutor.discussions[exerciseId];
      const firstAttempt = !checkedProblems[exerciseId] && !conversation;

      // A help conversation is not the tutor correcting anybody. The child
      // opened it themselves, before answering, read it, and then answered — so
      // there is nothing new on screen for them to stay and read, and leaving
      // them there made „Noriu suprasti" cost a press of „Toliau" that a plain
      // correct answer does not. `blocks` already reads a help conversation this
      // way: it owes the app nothing. A wrong answer inside one flips its branch
      // to DISCUSSION (`resume_after_help!`), so a second attempt after a real
      // correction still stays put.
      const carriesOnward =
        !checkedProblems[exerciseId] && (!conversation || conversation.branch === "HELP");
      const onCorrect = () => {
        if (carriesOnward) advanceAfterCorrect(exerciseId);
      };

      // Stop the clock before anything is graded, and report the reading with
      // the answer. The server decides what it means — a client cannot claim it
      // beat a limit it does not get to set.
      //
      // Only the first attempt is timed. A second one is answered by a child who
      // has already been told they were wrong and often talked it through, so a
      // reading from it measures the conversation rather than their recall.
      const timingThisOne =
        firstAttempt && clockRef.current.exerciseId === exerciseId && !clockRef.current.voided;
      const elapsedSeconds = timingThisOne ? Math.round(clockRef.current.elapsed) : undefined;
      // The client knows the limit too, so lateness is shown at once rather than
      // after a round trip — a child who watched the bar empty should not see
      // „Teisingai!" for a second and then have it change under them. The server
      // still decides the stored grade, and the reply reconciles below.
      const wasLate = timingThisOne && clockRef.current.expired;
      clockRef.current.stop();

      if (answerType === "fill_blanks") {
        let expected: string[];
        try {
          expected = JSON.parse(expectedAnswer);
        } catch {
          return;
        }
        const userSubs = expected.map((_, i) => answerAt(`${exerciseId}__${i}`));
        if (!userSubs.some((a) => a.trim())) return;

        const { results, overall } = checkFillBlanksAdvanced(userSubs, expected, rules);
        setCheckedProblems((prev) => ({ ...prev, [exerciseId]: overall }));
        setSubAnswerResults((prev) => ({ ...prev, [exerciseId]: results }));
        const joinedAnswer = userSubs.join("|");
        checkStudentAnswer({ variables: { sessionId: id, exerciseId, answer: joinedAnswer, result: overall, elapsedSeconds } }).catch(() => {});
        if (overall === "INCORRECT") {
          revealTheoryFor(exerciseId);
          tutor.start(exerciseId, joinedAnswer);
        } else if (overall === "PARTIAL") {
          setRevealedHints((prev) => new Set(prev).add(exerciseId));
          revealTheoryFor(exerciseId);
        } else {
          onCorrect();
        }
        return;
      }

      const multiAnswers = detectMultiAnswers(expectedAnswer, problemText, choices);

      if (multiAnswers) {
        const userSubs = multiAnswers.map((_, i) => answerAt(`${exerciseId}__${i}`));
        if (!userSubs.some((a) => a.trim())) return;

        const { results, overall } = checkMultiAnswerAdvanced(userSubs, multiAnswers, rules);
        setCheckedProblems((prev) => ({ ...prev, [exerciseId]: overall }));
        setSubAnswerResults((prev) => ({ ...prev, [exerciseId]: results }));
        const joinedAnswer = userSubs.join(", ");
        checkStudentAnswer({ variables: { sessionId: id, exerciseId, answer: joinedAnswer, result: overall, elapsedSeconds } }).catch(() => {});
        if (overall === "INCORRECT") {
          revealTheoryFor(exerciseId);
          tutor.start(exerciseId, joinedAnswer);
        } else if (overall === "PARTIAL") {
          setRevealedHints((prev) => new Set(prev).add(exerciseId));
          revealTheoryFor(exerciseId);
        } else {
          onCorrect();
        }
      } else {
        const userAnswer = directAnswer || answerAt(exerciseId);
        if (!userAnswer?.trim()) return;
        const result =
          checkChoiceAnswer(userAnswer, expectedAnswer, choices, rules) ??
          checkAnswerAdvanced(userAnswer, expectedAnswer, rules);
        // Right, but the clock had run out. The answer is still right and the
        // screen still says so — what changes is that this does not count as
        // knowing it by heart, which is the whole skill on these questions.
        const lateCorrect = result === "CORRECT" && wasLate;
        setCheckedProblems((prev) => ({ ...prev, [exerciseId]: lateCorrect ? "PARTIAL" : result }));
        if (lateCorrect) setLateProblems((prev) => new Set(prev).add(exerciseId));

        checkStudentAnswer({
          variables: { sessionId: id, exerciseId, answer: userAnswer, result, elapsedSeconds },
        })
          .then((response) => {
            // The server is the authority on both, so a clock that drifted or a
            // limit that changed under a cached page cannot leave the screen
            // saying one thing and the gradebook another.
            const stored = response.data?.checkStudentAnswer?.result;
            const late = response.data?.checkStudentAnswer?.answeredLate;
            if (stored) setCheckedProblems((prev) => ({ ...prev, [exerciseId]: stored as Verdict }));
            setLateProblems((prev) => {
              const next = new Set(prev);
              late ? next.add(exerciseId) : next.delete(exerciseId);
              return next;
            });
          })
          .catch(() => {});

        if (result === "INCORRECT") {
          revealTheoryFor(exerciseId);
          tutor.start(exerciseId, userAnswer);
        } else if (result === "CORRECT" && !lateCorrect) {
          // A late answer stays put. The advance exists to keep a child moving
          // when nothing needs reading, and „nespėjai" needs reading.
          onCorrect();
        }
      }
    },
    [answers, answerFormats, checkedProblems, advanceAfterCorrect, checkStudentAnswer, id, revealTheoryFor, tutor, session, theoryView]
  );

  // An exercise is a question, so this is the same number the header counts
  // slides with. They used to be computed differently — questions here, slides
  // there — and a question whose file could not be loaded made them disagree
  // („Užduotis 10 / 10" above „0 / 9 teisingai").
  const totalProblems = session?.exercises.length ?? 0;

  // What can actually be answered. A question whose file is missing has no
  // expected answer, and waiting for it would leave the session unfinishable.
  const answerableCount = session
    ? session.exercises.filter((ex: Exercise) => ex.expectedAnswer).length
    : 0;

  // What today was about, said once at the end. The same rules the panel at the
  // top of a slide offers before anything has happened, except here the learner
  // has already done the work they describe — so they cost no attention that
  // was wanted elsewhere, and they read as "look what you worked out".
  const learnedToday = useMemo(() => {
    if (!session) return [] as { id: string; title: string; theory: string }[];

    const seen = new Set<string>();
    const summaries: { id: string; title: string; theory: string }[] = [];
    for (const exercise of session.exercises as Exercise[]) {
      if (exercise.category !== "new" && exercise.category !== "struggling") continue;
      if (!hasTheoryKeyPoints(exercise.theory)) continue;

      const key = exercise.skillKey || exercise.id;
      if (seen.has(key)) continue;
      seen.add(key);
      summaries.push({ id: exercise.id, title: exercise.title, theory: exercise.theory! });
    }
    return summaries;
  }, [session]);

  const checkedCount = Object.keys(checkedProblems).length;
  const correctCount = Object.values(checkedProblems).filter((r) => r === "CORRECT").length;
  const partialCount = Object.values(checkedProblems).filter((r) => r === "PARTIAL").length;
  // A skipped question is finished with, even though it is not answered — so it
  // counts towards being allowed to hand the session in, and nowhere else. Left
  // out, a child who skipped one could never reach „Pateikti" and the only way
  // out of the session would be to abandon it, which counts for nothing at all.
  const skippedCount = session
    ? session.exercises.filter(
        (ex: Exercise) => ex.expectedAnswer && skipped.has(ex.id) && !checkedProblems[ex.id]
      ).length
    : 0;
  const settledCount = checkedCount + skippedCount;
  const allSettled = settledCount === answerableCount && answerableCount > 0;
  // The optional rule a grown-up switches on per learner: a wrong answer has to
  // be talked through before the session may be handed in. `DiscussionGate` on
  // the server refuses `completeSession` on the same reading, so a stale tab
  // cannot walk past it; the review screen says which questions and why.
  const owesDiscussion =
    !!session?.requiresDiscussionResolution &&
    (session.exercises as Exercise[]).some((ex: Exercise) => tutor.owesDiscussion(ex));
  // Neither answered nor left behind — the only thing standing between the
  // review screen and the finish button, and the one thing that screen names
  // out loud. A disabled button explains nothing.
  const openCount = Math.max(0, answerableCount - settledCount);

  // Status of an exercise (for the progress rail).
  function exerciseStatus(exercise: Exercise): "pending" | "correct" | "partial" | "incorrect" | "skipped" {
    if (!exercise.expectedAnswer) return "pending";
    const result = checkedProblems[exercise.id];
    if (!result) return skipped.has(exercise.id) ? "skipped" : "pending";
    if (result === "CORRECT") return "correct";
    if (result === "INCORRECT") return "incorrect";
    return "partial";
  }

  // „Praleisti" — the child leaves a question behind. It costs nothing in the
  // grades (Exercise::UNANSWERED), which is what makes it safe to offer: until
  // now a stuck learner's only exits were to guess wrong or to abandon the
  // session. Marked locally first, so the press is answered instantly.
  async function handleSkip(exerciseId: string) {
    setSkipped((prev) => new Set(prev).add(exerciseId));
    setReportOpen((prev) => (prev === exerciseId ? null : prev));
    // Moving on is the whole gesture, so it does not wait on the round trip. A
    // failure below rolls the skip back and says so — the question is still
    // there on the rail, unanswered, which is exactly what it would have been.
    if (hasNextSlide()) goToNextSlide();
    try {
      const { data: result } = await skipExercise({ variables: { sessionId: id, exerciseId } });
      if (!(result as any)?.skipExercise?.success) {
        const errors = (result as any)?.skipExercise?.errors ?? [];
        toast.error(errors[0] ?? "Nepavyko praleisti");
        setSkipped((prev) => {
          const next = new Set(prev);
          next.delete(exerciseId);
          return next;
        });
        return;
      }
      // The server has put this question to rest — see StuckExercises. Worth
      // telling her: adapting silently leaves the experience as „the app keeps
      // giving me the thing I cannot do", and a question that stops coming back
      // only reassures if somebody said it would stop.
      if ((result as any)?.skipExercise?.parked) {
        setParked((prev) => new Set(prev).add(exerciseId));
      }
    } catch {
      toast.error("Nepavyko praleisti");
      setSkipped((prev) => {
        const next = new Set(prev);
        next.delete(exerciseId);
        return next;
      });
    }
  }

  // A reported question is set aside: the server has made it SKIPPED and will
  // not grade it again (ReportExerciseProblem), so the screen drops its verdict
  // and treats it as skipped — settled, counting nowhere, and locked, since an
  // answer typed now would not be marked. A finished session is left graded on
  // the server, and there is no screen to update once it is finished.
  function handleReported(exerciseId: string) {
    setReported((prev) => new Set(prev).add(exerciseId));
    setSkipped((prev) => new Set(prev).add(exerciseId));
    setCheckedProblems((prev) => {
      if (!(exerciseId in prev)) return prev;
      const next = { ...prev };
      delete next[exerciseId];
      return next;
    });
  }

  // "I think I was right." Records the dispute for a grown-up to judge; nothing
  // is regraded here, so the student cannot mark their own answer correct.
  async function handleRequestRecheck(exerciseId: string) {
    setRecheckRequested((prev) => new Set(prev).add(exerciseId));
    try {
      const { data: result } = await requestRecheck({
        variables: { sessionId: id, exerciseId },
      });
      if ((result as any)?.requestAnswerRecheck?.success) {
        toast.success("Paprašėme suaugusiojo patikrinti");
      } else {
        const errors = (result as any)?.requestAnswerRecheck?.errors ?? [];
        toast.error(errors[0] ?? "Nepavyko išsiųsti");
        setRecheckRequested((prev) => {
          const next = new Set(prev);
          next.delete(exerciseId);
          return next;
        });
      }
    } catch {
      toast.error("Nepavyko išsiųsti");
      setRecheckRequested((prev) => {
        const next = new Set(prev);
        next.delete(exerciseId);
        return next;
      });
    }
  }

  async function handleSubmit() {
    // Settled, not answered: a session every question of which was skipped is
    // allowed to finish. It counts for nothing (no attempted questions, so no
    // proficiency movement and no credits), and refusing it would leave the
    // child with no way out of the session but to abandon it — which counts for
    // nothing either, and loses the record of what they skipped.
    if (settledCount === 0) {
      toast.error("Patikrinkite bent vieną atsakymą");
      return;
    }

    // The button is already disabled for this, so reaching here means the state
    // moved under a press. Says the same sentence the server would refuse with,
    // rather than letting the round trip come back as a bare failure.
    if (owesDiscussion) {
      toast.error("Dar reikia aptarti klaidingus atsakymus pokalbyje — tada galėsi baigti.");
      return;
    }
    try {
      const answersWithUnits = Object.fromEntries(
        Object.entries(answers).map(([key, value]) => [key, composeAnswer(value, answerFormats[key])])
      );
      const { data: result } = await submitAnswers({ variables: { sessionId: id, answers: answersWithUnits } });
      if (!result?.submitStudentAnswers?.success) {
        const errors = result?.submitStudentAnswers?.errors || [];
        toast.error(errors[0] || "Nepavyko pateikti atsakymų");
        return;
      }

      setSubmitted(true);

      // The answers were already graded on the server as they were checked, so
      // there is nothing left for a grown-up to decide before the points are
      // real. Waiting for a review the learner cannot see or hurry would only
      // teach them that finishing does not count.
      const { data: completeData } = await completeSession({
        variables: { sessionId: id },
        refetchQueries: studentId
          ? [{ query: GET_STUDENT_AVATAR_CREDITS, variables: { id: studentId } }]
          : [],
        awaitRefetchQueries: true,
      });

      // Set whatever the completion returned: a session with nothing to award is
      // still a completed one, and the letter must not stay hidden because of it.
      setCompletionSettled(true);

      const gamification = completeData?.completeSession?.gamification;
      if (!gamification) return;

      setGamificationResult(gamification);
      const celebrationEvents = toCelebrationEvents(gamification);
      if (celebrationEvents.length > 0) {
        celebrateMultiple(celebrationEvents);
      } else {
        fireConfetti("default");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nepavyko pateikti atsakymų");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Sesija nerasta</p>
        <Button asChild className="mt-4">
          <Link to="/sessions">Grįžti į sąrašą</Link>
        </Button>
      </div>
    );
  }

  // The only reason to turn anyone away is that the answers are already in.
  // Where they go instead depends on what their shell has: a grown-up gets the
  // session page, a learner has no such page and goes home.
  //
  // This has to sit down here with the other guards, not up among the callbacks.
  // Returning before `learnedToday`'s useMemo meant a finished session rendered
  // one hook fewer than the loading render that preceded it, and React threw
  // "Rendered fewer hooks than expected" instead of redirecting — so opening any
  // completed session's solve page was a blank screen.
  if (session.status !== "GENERATED" && !submitted) {
    return <Navigate to={role === "student" ? "/" : `/sessions/${id}`} replace />;
  }

  // After submission view — the celebration, what was earned, and where to next.
  if (submitted) {
    return (
      <SessionComplete
        sessionId={id!}
        studentId={studentId}
        studentName={session.student?.name}
        isLearner={role === "student"}
        subjectKey={session.subjectKey}
        subjectName={session.subject?.name}
        subjectIcon={session.subject?.icon}
        suggestedExerciseCount={session.student?.suggestedExerciseCount}
        subjectExerciseLimit={
          (session.student?.subjectSessionLengths ?? []).find(
            (row: { subjectKey: string }) => row.subjectKey === session.subjectKey
          )?.count
        }
        correctCount={correctCount}
        partialCount={partialCount}
        totalProblems={totalProblems}
        gamification={gamificationResult}
        completed={completionSettled}
        learnedToday={learnedToday}
        studio={avatarStudio}
        newAvatarUrl={newAvatarUrl}
      />
    );
  }

  const exercises = session.exercises;
  const slideCount = exercises.length;
  // One past the last question: the review screen. `currentExercise` is
  // undefined there, which is what makes every per-question rule below — the
  // clock, the tutor's block, the focus — inert on it without being asked to be.
  const reviewSlide = slideCount;
  const onReview = currentSlide >= reviewSlide;
  const currentExercise: Exercise | undefined = exercises[currentSlide];
  // An unfinished conversation no longer holds the child on the question.
  //
  // It used to, everywhere but the last question, on the grounds that the chat is
  // right there and the child owes it a second attempt. What that produced is a
  // child who wants to move on being refused by a disabled button and a tooltip —
  // and a refusal cannot teach the thing it is refusing for. So „Toliau" is
  // always live, and the reason to stay is stated instead of enforced: finishing
  // the conversation is worth points where walking away from it is worth none
  // (see `unresolvedChat` below). A child who chooses to go on has still answered
  // the question, and the conversation is waiting where they left it.
  //
  // Adjudication still holds: that is an AI call in flight whose answer is about
  // to change this very screen, and it takes seconds.
  const blockedForward = currentExercise ? tutor.isAdjudicating(currentExercise.id) : false;
  // A conversation about a wrong answer, still open on the question on screen.
  // Not a help chat: nothing in one is graded, so there is nothing to be paid for
  // finishing it — `tutor.blocks` already draws that line.
  const unresolvedChat =
    !!currentExercise && !tutor.isAdjudicating(currentExercise.id) && tutor.blocks(currentExercise);
  // What that question is worth once it is worked out — quoted from the server's
  // own rate rather than written into the sentence, for the reason the review
  // screen quotes it: a number a child is then paid less than is worse than no
  // number at all.
  const partialAnswerPoints = session.pointsRules?.partialAnswer ?? 0;

  // What the tutor's own „move on" button promises. From the last question there
  // is no next question — what comes instead is the review screen, so it names
  // that rather than „Baigti sesiją", which is the over-promise the navigation
  // bar was making one button along: the child pressed it and was handed one
  // more screen with a finish button on it.
  const nextLabelFor = (slide: number) => (slide === slideCount - 1 ? "Į pabaigą" : "Kita užduotis");

  // ── Question renderer ──
  // One exercise, one question, one slide — so the number a child reads is
  // simply which slide they are on.
  function renderProblem(exercise: Exercise, questionNumber: number) {
    const result = checkedProblems[exercise.id];
    const isChecked = !!result;
    const isCorrect = result === "CORRECT";
    const isPartial = result === "PARTIAL";
    // Right, but the clock beat them. A different thing from „partly right",
    // and the two share the PARTIAL grade — so the message has to separate them.
    const isLate = isPartial && lateProblems.has(exercise.id);
    // Left behind. Not a verdict, so it colours nothing and scores nothing — and
    // answering it anyway is what takes it back, which is why the inputs below
    // stay live.
    const isSkipped = !isChecked && skipped.has(exercise.id);
    const isReported = reported.has(exercise.id);
    const isFillBlanks = exercise.answerType === "fill_blanks";
    const fillBlanksSegments = isFillBlanks ? splitFillBlanks(exercise.problemText ?? "") : null;
    const fillBlanksCount = isFillBlanks ? fillBlanksSegments!.filter((s) => s.type === "blank").length : 0;
    const isChoice = !isFillBlanks && isChoiceProblem(exercise.choices);
    const multiAnswers = !isFillBlanks && exercise.expectedAnswer
      ? detectMultiAnswers(exercise.expectedAnswer, exercise.problemText ?? "", exercise.choices)
      : null;
    const isMulti = !!multiAnswers;
    const hasAnswer = isFillBlanks
      ? Array.from({ length: fillBlanksCount }, (_, i) => answers[`${exercise.id}__${i}`]?.trim()).some(Boolean)
      : isMulti
        ? multiAnswers!.some((_, i) => answers[`${exercise.id}__${i}`]?.trim())
        : !!(answers[exercise.id]?.trim());
    const subResults = subAnswerResults[exercise.id];
    const problemDiscussion = tutor.discussions[exercise.id];
    const hasOpenDiscussion = problemDiscussion?.status === "OPEN";
    const isAdjudicating = tutor.isAdjudicating(exercise.id);
    // Hide the direct answer while the AI is helping the student learn it.
    const suppressAnswer = isAdjudicating || hasOpenDiscussion;
    // Whether the panel's own "move on" button has anywhere to lead; the panel
    // decides when to show it (answer accepted, or the conversation is over).
    const canLeaveAfterDiscussion = !!problemDiscussion && hasNextSlide();
    const discussionPanel = problemDiscussion && !isReported ? (
      <DiscussionPanel
        discussion={problemDiscussion}
        sending={tutor.isSending(exercise.id)}
        onSend={(message) => tutor.send(exercise.id, message)}
        onResolve={(skipped) => tutor.resolve(exercise.id, skipped)}
        onNext={canLeaveAfterDiscussion ? goToNextSlide : undefined}
        nextLabel={nextLabelFor(currentSlide)}
        // A way back to the answer that does not end the conversation, for the
        // child who works it out three turns in. The exit below it returns them
        // too (see `onClosed`), so this is the one that keeps the chat.
        onBackToAnswer={() => returnToAnswer(exercise.id)}
      />
    ) : null;
    // The help ladder: the concept card first — free, static, and aimed at this
    // question's concept — then the tutor, which costs an AI call and turns a
    // correct answer into PARTIAL.
    // A concept introduction starts at step 1: its card is open from the moment
    // the question appears, because the reading IS the task. Everything else
    // still starts closed and is opened by the child pressing „Noriu suprasti" —
    // this is the one question type whose help was never optional.
    //
    // Done by moving the DEFAULT rather than by a second piece of state, so the
    // one thing a child can still do is close it: it is their card, and a panel
    // that reopens itself is one they stop reading.
    const isIntroduction = !!exercise.isIntroduction;
    // Rung 2 of the help ladder, asked for before answering. It goes ABOVE the
    // answer field, where rung 1's concept card already sits and for the same
    // reason the card gives: it is an explanation of the question, and the child
    // reads the question, then the explanation, then answers — without leaving
    // anything behind. Below it, a conversation the child has just worked through
    // has pushed the field they were getting ready to fill in off the top of the
    // screen, and the panel's own „bandau atsakyti →" pointed a screen upwards.
    //
    // A conversation opened by a WRONG answer stays below: there the answer and
    // its verdict come first, and the chat is what follows them.
    //
    // A help conversation folded into a discussion by a later wrong answer
    // (`resume_after_help!`) therefore moves from one side of the field to the
    // other, remounting the panel and re-firing its scroll-into-view. That is
    // right rather than incidental: the answer and the verdict now come first,
    // and the child is in the answer field at that moment, not typing in the chat.
    const isHelpChat = problemDiscussion?.branch === "HELP";
    const helpStep = helpSteps[exercise.id] ?? (isIntroduction ? 1 : 0);
    // The hint stands in only where there is no worked example at all (25 of the
    // 134 skills with questions have no theory.html). Where theory exists the
    // hint is deliberately not offered: measured over 3,000 level-1 hints, 27%
    // open by naming the operation to perform and 6% state the answer outright,
    // and on a word exercise choosing the operation is the skill being assessed.
    // It survives for a grown-up in the session detail view.
    const cardHtml = hasTheoryExample(exercise.theory) ? exercise.theory ?? null : null;
    const hasStaticHelp = !!cardHtml || !!exercise.hint;
    const isConceptExpanded = conceptExpanded[exercise.id] ?? false;
    // Only offered where the full theory holds something the card left out — on a
    // theory file that is one worked example, the card already *is* the file.
    const canExpandConcept = hasTheoryBeyondExample(cardHtml);
    // Either role: revealed after the answer, or standing in for a card the
    // skill has no theory to build. One box, so the two can't stack.
    const hintHtml =
      revealedHints.has(exercise.id) || (helpStep >= 1 && !cardHtml) ? exercise.hint ?? null : null;
    // Nothing to reveal on an ordinary question, which is all of them: only an
    // authored concept introduction carries one. No client state and no restore
    // pass — it rides on the exercise and `checkedProblems` is already rebuilt
    // from the server, so a reload puts it back exactly as it was.
    const revealHtml = exercise.reveal ?? null;

    return (
      <div
        key={exercise.id}
        // Always big (see .exercise-active in globals.css: larger text and larger
        // diagrams). Answering does not shrink it — a student rereading what they
        // just got right, or working out what went wrong, needs it just as legible
        // as before. Only the colour changes.
        // `bg-card` rather than inheriting the page: the app background carries
        // a faint blue tint, and a question is read for minutes at a time on a
        // screen full of figures drawn on white. The white box is the paper.
        className={`exercise-active rounded-lg border p-6 transition-all ${
          isChecked
            ? isCorrect
              ? "border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/30"
              : isPartial
                ? "border-yellow-300 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/30"
                : "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30"
            : "bg-card"
        }`}
      >
        <div className="flex items-start gap-4">
          <span className="font-medium text-muted-foreground flex items-center gap-1">
            {isChecked &&
              (isCorrect ? (
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : isPartial ? (
                <Minus className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
              ) : (
                <X className="h-4 w-4 text-red-600 dark:text-red-400" />
              ))}
            {isSkipped && <SkipForward className="h-4 w-4" />}
            {questionNumber}.
          </span>
          <div className="flex-1 space-y-2">
            {/* Says what kind of moment this is before the child reads the
                question. „Prisiminkim" is deliberately not a demotion and not a
                verdict — the same reason review skills are labelled
                „(prisimink)" rather than by how badly they went. */}
            {isIntroduction && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                <Sparkles className="h-3.5 w-3.5" />
                {exercise.introductionKind === "review" ? "Prisiminkim" : "Nauja sąvoka"}
              </span>
            )}
            {isFillBlanks ? (
              <div className="font-medium">
                {(() => {
                  let blankIdx = 0;
                  return fillBlanksSegments!.map((segment, segIdx) => {
                    if (segment.type === "text") {
                      return (
                        <MathContent key={segIdx} html={segment.value} className="inline" zoomableFigures />
                      );
                    }
                    const bi = blankIdx++;
                    const subKey = `${exercise.id}__${bi}`;
                    const subResult = subResults?.[bi];
                    const subChecked = isChecked && subResult !== undefined;
                    const subCorrect = subResult === "CORRECT";
                    return (
                      <span key={segIdx} className="inline-flex items-center mx-0.5">
                        <input
                          ref={(el) => {
                            if (el) {
                              inputRefs.current.set(subKey, el);
                              if (bi === 0) inputRefs.current.set(exercise.id, el);
                            } else {
                              inputRefs.current.delete(subKey);
                              if (bi === 0) inputRefs.current.delete(exercise.id);
                            }
                          }}
                          type="text"
                          value={answers[subKey] || ""}
                          disabled={isChecked || isReported}
                          onChange={(e) => setAnswers((prev) => ({ ...prev, [subKey]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              if (bi < fillBlanksCount - 1) {
                                const nextInput = inputRefs.current.get(`${exercise.id}__${bi + 1}`);
                                if (nextInput) nextInput.focus();
                              } else if (hasAnswer && exercise.expectedAnswer) {
                                checkProblem(exercise.id, exercise.expectedAnswer!, exercise.problemText, undefined, exercise.answerType);
                              }
                            }
                          }}
                          style={{ width: `${Math.max(2, segment.length + 1)}ch` }}
                          className={`inline-block text-center border-b-2 border-t-0 border-x-0 bg-transparent outline-none px-0.5 font-medium ${
                            subChecked
                              ? subCorrect
                                ? "border-green-500 text-green-700 dark:text-green-400"
                                : "border-red-500 text-red-700 dark:text-red-400"
                              : "border-gray-400 dark:border-gray-500"
                          }`}
                        />
                        <AnswerAffix value={answers[subKey] || ""} format={answerFormats[subKey]} />
                        {subChecked && !subCorrect && (
                          <span className="text-xs text-green-700 dark:text-green-400 ml-1">
                            ({(() => { try { return JSON.parse(exercise.expectedAnswer!)[bi]; } catch { return ""; } })()})
                          </span>
                        )}
                      </span>
                    );
                  });
                })()}
              </div>
            ) : isChoice ? (
              <ChoiceProblem
                questionHtml={exercise.problemText ?? ""}
                choices={exercise.choices!}
                className="font-medium"
                zoomableFigures
                selectedAnswer={answers[exercise.id]}
                disabled={isChecked || isReported}
                onSelectAnswer={(value) => {
                  if (isChecked) return;
                  setAnswers((prev) => ({ ...prev, [exercise.id]: value }));
                  if (value && exercise.expectedAnswer) {
                    checkProblem(
                      exercise.id,
                      exercise.expectedAnswer!,
                      exercise.problemText,
                      value,
                      exercise.answerType,
                      exercise.choices
                    );
                  }
                }}
                firstButtonRef={(el) => {
                  if (el) inputRefs.current.set(exercise.id, el);
                  else inputRefs.current.delete(exercise.id);
                }}
              />
            ) : (
              <MathContent html={exercise.problemText ?? ""} className="font-medium" zoomableFigures />
            )}

            {/* The countdown, on the few questions where answering fast IS the
                skill. Directly under the question and above the answer, so it is
                in view while they are typing rather than somewhere they have to
                look for it.

                Only for the question actually on screen: the clock belongs to
                the current slide, and drawing it under a question the child is
                not on would show them somebody else's seconds. */}
            {!isChecked && !isSkipped && exercise.id === clockExercise?.id && (
              <QuestionClockBar clock={questionClock} />
            )}

            {/* The concept card — this question's worked example, the explanation
                of the concept behind it, and the rule to remember. One box, two
                ways in: „Noriu suprasti" before answering (rung 1 of the help
                ladder), or a wrong answer, which opens it by itself.

                Directly under the question and ABOVE the answer, deliberately.
                The alternative — the theory accordion at the top of the slide —
                scrolls the question, the answer and the correction out of view at
                exactly the moment all three are what the explanation is about.
                Here the child reads the question, then the one idea behind it,
                then what they wrote, without leaving anything behind.

                Stays once opened, including after the answer is checked or the
                tutor joins: it is what the child was reading. */}
            {helpStep >= 1 && cardHtml && (
              <div id={`help-${exercise.id}`} className="mt-3 scroll-mt-20 rounded border bg-muted/30 p-3">
                {/* Named, because a wrong answer opens this box without being
                    asked: unlabelled prose appearing between the question and the
                    answer reads as part of the question.

                    The expander lives up here rather than under the prose so that
                    the way back is where the way in was. Expanded, this skill's
                    whole theory is eight worked examples and every concept it
                    teaches — a control at the bottom of that is a control the
                    child has to go looking for, having expanded it by accident. */}
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                    <GraduationCap className="h-4 w-4" />
                    Kaip tai veikia
                  </span>
                  {canExpandConcept && (
                    <button
                      type="button"
                      onClick={() =>
                        setConceptExpanded((prev) => ({
                          ...prev,
                          [exercise.id]: !isConceptExpanded,
                        }))
                      }
                      className="inline-flex flex-shrink-0 items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 cursor-pointer"
                    >
                      {isConceptExpanded ? (
                        <>
                          <ChevronUp className="h-3.5 w-3.5" />
                          Rodyti tik šią dalį
                        </>
                      ) : (
                        <>
                          <BookOpen className="h-3.5 w-3.5" />
                          Rodyti visą teoriją
                        </>
                      )}
                    </button>
                  )}
                </div>
                <TheoryView
                  html={cardHtml}
                  className="text-sm"
                  // Narrowed to this question's concept until asked otherwise.
                  conceptOnly={!isConceptExpanded}
                  concepts={exercise.concepts}
                  zoomableFigures
                />
                {/* Expanded, the way out is a long way up. Repeated here so it is
                    also where the reading ends. */}
                {canExpandConcept && isConceptExpanded && (
                  <button
                    type="button"
                    onClick={() =>
                      setConceptExpanded((prev) => ({ ...prev, [exercise.id]: false }))
                    }
                    className="mt-2 inline-flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 cursor-pointer"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                    Rodyti tik šią dalį
                  </button>
                )}
              </div>
            )}

            {/* The help conversation, above everything used to answer with. See
                `isHelpChat`. */}
            {isHelpChat && discussionPanel}

            {/* Fill-the-blanks check button */}
            {isFillBlanks && !isChecked && exercise.expectedAnswer && (
              <div className="mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!hasAnswer || isReported}
                  onClick={() => checkProblem(exercise.id, exercise.expectedAnswer!, exercise.problemText, undefined, exercise.answerType)}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Tikrinti
                </Button>
              </div>
            )}

            {/* Multi-answer inputs */}
            {isMulti && exercise.expectedAnswer && !isChoice && (
              <div className="mt-3 space-y-2">
                {multiAnswers!.map((expected, i) => {
                  const subKey = `${exercise.id}__${i}`;
                  const subResult = subResults?.[i];
                  const subChecked = isChecked && subResult !== undefined;
                  const subCorrect = subResult === "CORRECT";
                  const label = String.fromCharCode(97 + i) + ")";
                  return (
                    <div key={subKey} className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground w-6">{label}</span>
                      <AnswerInput
                        ref={(el) => {
                          if (el) {
                            inputRefs.current.set(subKey, el);
                            if (i === 0) inputRefs.current.set(exercise.id, el);
                          } else {
                            inputRefs.current.delete(subKey);
                            if (i === 0) inputRefs.current.delete(exercise.id);
                          }
                        }}
                        placeholder={`${label} atsakymas...`}
                        value={answers[subKey] || ""}
                        format={answerFormats[subKey]}
                        disabled={isChecked || isReported}
                        onValueChange={(value) => setAnswers((prev) => ({ ...prev, [subKey]: value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            if (i < multiAnswers!.length - 1) {
                              const nextInput = inputRefs.current.get(`${exercise.id}__${i + 1}`);
                              if (nextInput) nextInput.focus();
                            } else if (hasAnswer) {
                              checkProblem(exercise.id, exercise.expectedAnswer!, exercise.problemText);
                            }
                          }
                        }}
                        className={`max-w-xs ${
                          subChecked
                            ? subCorrect
                              ? "border-green-400 dark:border-green-700"
                              : "border-red-400 dark:border-red-700"
                            : ""
                        }`}
                      />
                      {subChecked &&
                        (subCorrect ? (
                          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <X className="h-4 w-4 text-red-600 dark:text-red-400" />
                        ))}
                      {subChecked && !subCorrect && (
                        <span className="text-sm text-green-700 dark:text-green-400">→ {expected}</span>
                      )}
                    </div>
                  );
                })}
                {!isChecked && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!hasAnswer || isReported}
                    className="mt-1"
                    onClick={() => checkProblem(exercise.id, exercise.expectedAnswer!, exercise.problemText)}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Tikrinti
                  </Button>
                )}
              </div>
            )}

            {/* Single answer input + check button */}
            {exercise.expectedAnswer && !isChoice && !isMulti && !isFillBlanks && (
              <div className="mt-3 flex items-center gap-2">
                <AnswerInput
                  ref={(el) => {
                    if (el) inputRefs.current.set(exercise.id, el);
                    else inputRefs.current.delete(exercise.id);
                  }}
                  placeholder="Įrašyk atsakymą..."
                  value={answers[exercise.id] || ""}
                  format={answerFormats[exercise.id]}
                  disabled={isChecked || isReported}
                  onValueChange={(value) => setAnswers((prev) => ({ ...prev, [exercise.id]: value }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && hasAnswer) {
                      checkProblem(exercise.id, exercise.expectedAnswer!, exercise.problemText);
                    }
                  }}
                  className={`max-w-xs ${
                    isChecked
                      ? isCorrect
                        ? "border-green-400 dark:border-green-700"
                        : "border-red-400 dark:border-red-700"
                      : ""
                  }`}
                />
                {!isChecked && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!hasAnswer || isReported}
                    onClick={() => checkProblem(exercise.id, exercise.expectedAnswer!, exercise.problemText)}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Tikrinti
                  </Button>
                )}
              </div>
            )}

            {/* Result feedback */}
            {isChecked && (
              <div
                className={`mt-2 text-sm font-medium ${
                  isCorrect
                    ? "text-green-700 dark:text-green-400"
                    : isPartial
                      ? "text-yellow-700 dark:text-yellow-400"
                      : "text-red-700 dark:text-red-400"
                }`}
              >
                {isCorrect
                  ? "Teisingai!"
                  : isLate
                    ? "Teisingai! Bet nespėjai laiku."
                    : isPartial && subResults
                      ? `Iš dalies teisingai (${subResults.filter((r) => r === "CORRECT").length}/${subResults.length})`
                      : "Neteisingai"}
              </div>
            )}

            {/* Why a right answer is marked yellow. The countdown is gone by now
                — and after a reload it was never on screen at all — so the grade
                would otherwise be exactly the unexplained penalty that showing
                the clock in the first place exists to avoid.

                Worded as something that comes back rather than something lost:
                the question returns in a later session, which is true, and is
                the entire purpose of grading it this way. */}
            {isChecked && isLate && (
              <p className="mt-1 text-xs text-muted-foreground">
                Šitą reikia mokėti mintinai — dar kartą sutiksi šią užduotį.
              </p>
            )}

            {/* AI adjudication in progress */}
            {isAdjudicating && (
              <div className="mt-2 flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400">
                <Sparkles className="h-4 w-4 animate-pulse" />
                Tikriname tavo atsakymą...
              </div>
            )}

            {/* Discussion (Branch B) — after the answer and the verdict, which is
                what it is about. The help conversation is the same panel on the
                other side of the answer field; see `discussionPanel`. */}
            {!isHelpChat && discussionPanel}

            {/* Show correct answer when incorrect — only once the AI is done */}
            {/* The payoff of a concept introduction, shown once the child has
                answered — however they answered.

                This is the one thing an introduction has that no other question
                does, and it is not a hint. A hint appears only on a miss and is
                revealed *instead* of an explanation; here the reveal IS the
                teaching, and the child who got it right needs it just as much:
                the count turning into 4 × 3, the 10×10 millimetre grid, the two
                equations side by side. That is why it is a field of its own on
                the question rather than the `hint` reused — see
                ConceptIntroBlock.

                Indigo and Sparkles on purpose: the same pair as the „Nauja
                sąvoka" chip at the top of this question, so the payoff plainly
                belongs to the moment the chip announced rather than reading as a
                fourth kind of feedback box. */}
            {isChecked && revealHtml && !suppressAnswer && (
              <div className="mt-2 p-3 rounded bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800">
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
                  {/* The reveal carries the same $…$ maths and the same `/svg/`
                      figures the question does, so it goes through MathContent
                      rather than being printed as text — and its figures are
                      tappable, because a 10×10 grid is exactly the drawing a
                      child wants to look at closely. */}
                  <div className="intro-reveal text-sm text-indigo-900 dark:text-indigo-100">
                    <MathContent html={revealHtml} zoomableFigures />
                  </div>
                </div>
              </div>
            )}

            {/* Suppressed when a reveal is showing: the reveal always states the
                correct answer and then says why, so this box would print the same
                answer twice, two lines apart, in two different colours. */}
            {isChecked && !isCorrect && !isPartial && !isMulti && !isFillBlanks && exercise.expectedAnswer && !suppressAnswer && !revealHtml && (
              <div className="mt-1 p-2 rounded bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <div className="text-sm text-green-800 dark:text-green-200">
                  <span className="font-medium">Teisingas atsakymas:</span>{" "}
                  <MathContent html={exercise.expectedAnswer.split(";;")[0]} className="inline" />
                </div>
              </div>
            )}

            {/* The hint: revealed automatically after the answer, and standing in
                for rung 1 on the 25 of 134 skills with no theory.html. */}
            {hintHtml && (
              // The anchor rung 1 scrolls to, but only where the hint *is* rung 1:
              // with a card above it the card is what was just revealed, and two
              // elements sharing an id would leave which one moves to chance.
              <div
                id={cardHtml ? undefined : `help-${exercise.id}`}
                className="mt-2 scroll-mt-20 p-2 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
              >
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  {/* Hints carry the same $...$ math as the question — as plain
                      text a child reads the LaTeX source instead of the fraction. */}
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <span className="font-medium">Užuomina:</span>{" "}
                    <MathContent html={hintHtml} className="inline" />
                  </div>
                </div>
              </div>
            )}

            {/* One door out of being stuck, opened in two steps. Offered before
                the answer is checked only: afterwards the tutor arrives on its
                own and two of it help nobody.

                Never labelled „Nežinau" — that asks a child to make a statement
                about themselves at the moment they already feel worst, and it is
                a verdict where „Noriu suprasti" is an intention. Rung 2 is
                „Pasikalbėkim" for the same reason: an invitation, where „vis tiek
                nesuprantu" would be the child issuing the verdict instead.
                system_help.txt already forbids the tutor from telling a child
                they „nežino"; the button must not put the word in their mouth.

                The answer field behind all of this stays theirs to fill in —
                working the answer out is still the point.

                Not gated on `helpStep < 2`: a tutor that fails to open degrades
                silently to no panel, and hiding the button would leave the child
                who asked for help with nothing to press. While there is no
                conversation, „Pasikalbėkim" stays and retries. */}
            {/* Not on an introduction. Its card is already open — that is the
                whole task — so rung 1 is spent, and rung 2 would hand a reading
                question to a tutor forbidden from saying the answer, which here
                is the concept's own name. Nothing is taken away: the child has
                the explanation on screen, four options, and no marks at stake.
                AdjudicateAnswer refuses the same conversation server-side. */}
            {!isChecked && !problemDiscussion && !isIntroduction && !isReported && (
              <button
                type="button"
                onClick={() => {
                  // With nothing static to show, the first press goes straight to
                  // the tutor — which is what such a task offered before.
                  const next = helpStep === 0 && hasStaticHelp ? 1 : 2;
                  if (next === 2) tutor.requestHelp(exercise.id);
                  setHelpSteps((prev) => ({ ...prev, [exercise.id]: next }));
                  // Rung 1 renders below the button and moves nothing, so on a
                  // long question the press reads as nothing having happened —
                  // the only other feedback is the label changing. The chat
                  // panel scrolls itself into view on mount; the card has to be
                  // told to, and only after React has put it on the page.
                  // `nearest` so an already-visible card isn't yanked around.
                  if (next === 1) {
                    requestAnimationFrame(() =>
                      document
                        .getElementById(`help-${exercise.id}`)
                        ?.scrollIntoView({ behavior: "smooth", block: "nearest" })
                    );
                  }
                }}
                className="mt-2 inline-flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 cursor-pointer"
              >
                {helpStep === 0 ? (
                  <>
                    <GraduationCap className="h-3.5 w-3.5" />
                    Noriu suprasti
                  </>
                ) : (
                  <>
                    <MessageCircle className="h-3.5 w-3.5" />
                    Pasikalbėkim
                  </>
                )}
                {/* Said on the press that opens the tutor, before it opens: a
                    child is owed knowing the reply comes from an AI and not a
                    person (AI Act art. 50). The card on the first press is the
                    theory file and needs no label. */}
                {(helpStep > 0 || !hasStaticHelp) && <AiBadge />}
              </button>
            )}

            {/* A way to disagree. Small and quiet — most wrong answers really are
                wrong, and the AI has usually explained why by this point — but a
                learner who is actually right should not have to just accept it.
                Nothing is regraded here; it goes to a grown-up. */}
            {isChecked && !isCorrect && !suppressAnswer && (
              <div className="mt-3 text-sm">
                {recheckRequested.has(exercise.id) ? (
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <Check className="h-3.5 w-3.5" />
                    Suaugęs patikrins šį atsakymą
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleRequestRecheck(exercise.id)}
                    className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline cursor-pointer"
                  >
                    Manau, mano atsakymas teisingas — paprašyti, kad patikrintų suaugęs
                  </button>
                )}
              </div>
            )}

            {/* "Something is wrong with the question itself" — a different claim
                from the one above, which disputes the grading of a question
                that works. Available before answering too: a figure that will
                not draw is exactly the thing you cannot answer around. */}
            {/* Coming back to a skipped question is allowed, and nothing else
                would say so — the inputs above look exactly as they did. Never
                „neatsakei" or „praleidai, nes nemokėjai": a skip costs nothing
                in the grades, so the note must not charge for it either. */}
            {isSkipped && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <SkipForward className="h-3.5 w-3.5" />
                {isReported
                  ? "Ačiū, kad pranešei. Šios užduoties nevertinsime ir daugiau jos neduosime"
                  : parked.has(exercise.id)
                    ? "Šitą kol kas atidedame — kitą kartą duosime kitokią. Jei nori, vis tiek gali ją išspręsti"
                    : "Praleidai šią užduotį — jei nori, vis tiek gali ją išspręsti"}
              </div>
            )}

            <ReportProblem
              sessionId={id!}
              exerciseId={exercise.id}
              reported={reported.has(exercise.id)}
              open={reportOpen === exercise.id}
              onClose={() => setReportOpen(null)}
              onReported={handleReported}
            />
          </div>

          {/* What can be done *to* the question, as opposed to with it: leave it
              behind, or say it is broken. Both are quiet by design — neither is
              something to invite — and both are the wrong thing to put in the
              row of help buttons under the question, where they would compete
              with „Noriu suprasti" as ways of being stuck. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title="Užduoties veiksmai"
                aria-label="Užduoties veiksmai"
                className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              {/* Only an unanswered question can be skipped — the grade records
                  what they actually did, and there is nothing left to skip. */}
              {!isChecked && (
                <DropdownMenuItem
                  disabled={isSkipped}
                  onClick={() => {
                    if (!isSkipped) handleSkip(exercise.id);
                  }}
                >
                  <SkipForward className="mr-2 h-4 w-4" />
                  {isSkipped ? "Praleista" : "Praleisti užduotį"}
                  {isSkipped && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                disabled={isReported}
                onClick={() => {
                  if (!isReported) setReportOpen(exercise.id);
                }}
              >
                <Flag className="mr-2 h-4 w-4" />
                {isReported ? "Jau pranešta" : "Kažkas negerai su šia užduotimi"}
                {isReported && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
              {/* Reporting the QUESTION and asking about the APP are different
                  things, and folding them together loses the distinction that
                  matters when the queue is read: one is about a file every child
                  meets next, the other is about this account. */}
              <DropdownMenuItem onClick={() => setSupportOpen(true)}>
                <LifeBuoy className="mr-2 h-4 w-4" />
                Reikia pagalbos su programa
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }

  const newSkillName =
    session.sessionPlan?.find((p: { category?: string; skillName?: string }) => p.category === "new")
      ?.skillName || session.newSkillKey;

  return (
    <div className="pb-28">
      {session.newSkillKey && (
        <NewSkillModal
          isOpen={showNewSkillModal}
          onClose={() => setShowNewSkillModal(false)}
          skillName={newSkillName}
        />
      )}

      {/* Page level, not per question: one dialog, driven by the „…" menu of
          whichever question is on screen. It attaches the path, so the request
          carries which session they were in. */}
      <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />

      <div className="flex items-center gap-4 mb-4">
        {/* Where „back" goes depends on what the shell has, the same rule the
            finished-session guard above follows: a grown-up has a session page,
            a learner has no such route at all. Pointed at `/sessions/:id` for
            everyone, a learner leaving a question landed on a path their own
            route tree does not match — and `Button asChild` takes exactly one
            child, so the note sits outside it. */}
        <Button variant="ghost" size="icon" asChild>
          <Link to={role === "student" ? "/" : `/sessions/${id}`} aria-label="Atgal">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">Spręsk užduotis</h1>
          <p className="text-muted-foreground truncate">{session.student.name}</p>
        </div>
        <Badge variant="outline" className="text-sm">
          {correctCount} / {totalProblems} teisingai
        </Badge>
      </div>

      {/* Progress rail — muted overview of all exercises */}
      <div className="flex flex-wrap items-center justify-center gap-1.5 mb-6">
        {exercises.map((ex: Exercise, i: number) => {
          const status = exerciseStatus(ex);
          const isCurrent = i === currentSlide;
          const color =
            status === "correct"
              ? "bg-green-500"
              : status === "incorrect"
                ? "bg-red-500"
                : status === "partial"
                  ? "bg-yellow-500"
                  // Skipped is deliberately not a colour of its own: it is
                  // neither right nor wrong, and a rail full of grades is not
                  // the place to mark what a child chose to leave. Darker than
                  // pending only so „done with" is distinguishable from
                  // „not there yet".
                  : status === "skipped"
                    ? "bg-muted-foreground/60"
                    : "bg-muted-foreground/30";
          return (
            <button
              key={ex.id}
              type="button"
              aria-label={`Užduotis ${i + 1}`}
              onClick={() => setCurrentSlide(i)}
              className={`h-2.5 rounded-full transition-all ${color} ${
                isCurrent ? "w-8 ring-2 ring-primary ring-offset-2 ring-offset-background" : "w-2.5"
              }`}
            />
          );
        })}

        {/* The finish, on the rail like everything else — a child who can see
            where the end is knows how much is left of it. A flag rather than a
            dot: it is not a question and must not read as one more of them. */}
        <button
          type="button"
          aria-label="Baigti sesiją"
          onClick={() => setCurrentSlide(reviewSlide)}
          className={`ml-1 flex h-5 w-5 items-center justify-center rounded-full transition-all ${
            allSettled ? "bg-primary text-primary-foreground" : "bg-muted-foreground/30 text-background"
          } ${onReview ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`}
        >
          <Flag className="h-3 w-3" />
        </button>
      </div>

      {onReview && (
        <SessionReview
          questions={exercises.map((ex: Exercise, i: number) => ({
            id: ex.id,
            number: i + 1,
            status: !ex.expectedAnswer ? ("unanswerable" as const) : exerciseStatus(ex),
            talking: tutor.discussions[ex.id]?.status === "OPEN",
            owing: tutor.owesDiscussion(ex),
          }))}
          correctCount={correctCount}
          partialCount={partialCount}
          skippedCount={skippedCount}
          openCount={openCount}
          requiresDiscussionResolution={session.requiresDiscussionResolution}
          pointsRules={session.pointsRules}
          onGoTo={(slide) => {
            window.scrollTo({ top: 0, behavior: "smooth" });
            setCurrentSlide(slide);
          }}
        />
      )}

      {/* Current slide. No fixed width cap: diagrams, tables and worked examples
          get the width the screen actually has, the way the grown-up session
          view does. */}
      {currentExercise && (
        <div className="w-full space-y-4">
          {/* One question per page, so there is nothing for a skill card to
              group: the theory and the question stand on their own. The skill
              name stays as a quiet line above them — it says what this is about
              without competing with the question for attention. */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {currentExercise.title}
            </span>
            {currentExercise.category && (
              <SkillCategoryBadge
                category={currentExercise.category as "new" | "struggling" | "learning" | "review"}
                size="sm"
              />
            )}
          </div>
          {currentExercise.instructions && (
            <p className="text-sm text-muted-foreground">{currentExercise.instructions}</p>
          )}

          {/* Theory - collapsible */}
          {currentExercise.theory && (() => {
            const theoryKey = `theory-${currentExercise.skillKey || currentSlide}`;
            const isNew = currentExercise.category === "new";
            const isStruggling = currentExercise.category === "struggling";
            // Theory never opens by itself. A wall of explanation between the
            // child and the question is scrolled past, not read — measured at
            // 24% engagement when it did auto-open — and it pushes the answer
            // field off screen. The badge below still says the topic is new or
            // shaky, and one tap opens it; the concept card inside the question
            // („Noriu suprasti", or a wrong answer) is what a stuck child gets,
            // and „Rodyti visą teoriją" on that card is the way to all of this
            // without leaving the question.
            const isExpanded = theoryToggles[theoryKey] ?? false;
            // The nudge: this topic is new, and the child has neither opened the
            // theory nor got one of its questions right yet. Never while it is
            // open — a panel pulsing at somebody already reading it is noise.
            const nudgeKey = nudgeKeyOf(currentExercise);
            const nudging =
              isNew && !isExpanded && !theoryOpened.has(nudgeKey) && !nudgeSettled.has(nudgeKey);
            // Plain surface, same as the grown-up session view: a wall of
            // tinted background makes a long explanation harder to read, and
            // the diagrams inside bring their own colour. "Nauja tema"
            // carries the emphasis instead.
            return (
              <div
                id={theoryKey}
                className={`scroll-mt-20 rounded-lg border bg-card ${nudging ? "theory-nudge" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => {
                    // Latched here rather than read back off `isExpanded`: this is
                    // „they have taken the hint", and shutting the panel again is
                    // not un-reading it.
                    if (!isExpanded) setTheoryOpened((prev) => new Set(prev).add(nudgeKey));
                    setTheoryToggles((prev) => ({ ...prev, [theoryKey]: !isExpanded }));
                  }}
                  className="w-full p-4 flex items-center gap-2 text-left cursor-pointer"
                >
                  <BookOpen
                    className={`h-5 w-5 flex-shrink-0 ${
                      nudging
                        ? "theory-nudge-icon text-cyan-600 dark:text-cyan-400"
                        : "text-muted-foreground"
                    }`}
                  />
                  <span className="font-medium flex-1">
                    Teorija
                    {isNew && (
                      // While nudging it says what to do, not just what this is:
                      // „(nauja tema)" is a label, and a label is not a reason to
                      // press anything. It goes back to the quiet label the moment
                      // the nudge is settled.
                      <span
                        className={
                          nudging
                            ? "theory-nudge-label ml-2 inline-block rounded-full bg-cyan-100 px-2 py-0.5 text-xs font-semibold text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-200"
                            : "ml-2 text-sm font-normal text-cyan-600 dark:text-cyan-400"
                        }
                      >
                        {nudging ? "Nauja tema — pažiūrėk!" : "(nauja tema)"}
                      </span>
                    )}
                    {!isNew && isStruggling && (
                      <span className="ml-2 text-sm font-normal text-amber-600 dark:text-amber-400">
                        (prisimink)
                      </span>
                    )}
                  </span>
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown
                      className={`h-4 w-4 ${
                        nudging ? "theory-nudge-icon text-cyan-600 dark:text-cyan-400" : "text-muted-foreground"
                      }`}
                    />
                  )}
                </button>
                {/* `auto: false` — nothing opens the theory but the student. */}
                {isExpanded && (
                  <div
                    className="px-4 pb-4 pt-0"
                    ref={(node) => theoryView.attach(currentExercise.id, node, false)}
                  >
                    <TheoryView html={currentExercise.theory} className="text-sm" zoomableFigures />
                    <div className="mt-3 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          theoryView.markAcknowledged(currentExercise.id);
                          setTheoryToggles((prev) => ({ ...prev, [theoryKey]: false }));
                        }}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Supratau
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {renderProblem(currentExercise, currentSlide + 1)}
        </div>
      )}

      {/* Bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t shadow-lg z-10">
        {/* „Toliau" no longer refuses while a conversation is open, so this is
            what the refusal used to say — as a reason rather than a barrier, and
            in the one currency a child can weigh: a question worked out with the
            tutor is scored PARTIAL and paid for, where one left unresolved stays
            INCORRECT and is paid nothing. It sits above the button it is about,
            and only while there is something to say. */}
        {unresolvedChat && (
          <p className="mb-2 text-center text-xs font-medium text-amber-700 dark:text-amber-300">
            Pokalbis apie šią užduotį dar neužbaigtas. Išsiaiškink ją iki galo — gausi už ją
            {partialAnswerPoints ? ` ${partialAnswerPoints} taškus` : " taškų"}, o palikta
            neišsiaiškinta taškų neduos.
          </p>
        )}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => setCurrentSlide((s) => Math.max(0, s - 1))}
            disabled={currentSlide === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Atgal
          </Button>

          <span className="text-sm text-muted-foreground">
            {!onReview
              ? `Užduotis ${currentSlide + 1} / ${slideCount}`
              : allSettled
                ? "Viskas padaryta"
                : `Liko atsakyti: ${openCount}`}
          </span>

          {/* The finish button belongs in this bar, not at the foot of the
              summary above it: twenty-five questions push a button placed after
              them below the fold, and the whole point of it is that a child
              arriving at the end has one obvious thing to press. */}
          {onReview ? (
            <SessionFinishButton
              isLearner={role === "student"}
              isSubmitting={isSubmitting}
              canSubmit={allSettled && !owesDiscussion}
              onSubmit={handleSubmit}
            />
          ) : (
            <Button
              onClick={goToNextSlide}
              disabled={blockedForward}
              title={blockedForward ? "Palauk, dar tikriname tavo atsakymą" : undefined}
            >
              {/* „Toliau" on every question, the last one included: what follows
                  it is the review screen, and „Baigti" there promised an ending
                  that is one more press away — the child pressed it and was
                  handed another screen with a finish button on it. Ending the
                  session is what „Baigiu!" does, on that screen and nowhere
                  else. */}
              Toliau
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
